/**
 * DialogsPanel — вкладка «Диалоги».
 *
 * Зачем. Бот отвечает живым людям каждый день, а увидеть эти разговоры было
 * негде: диалоги пишутся в базу Тони, и до 13.08 прочитать их можно было только
 * запросом к базе руками. Значит, о качестве работы агента судили по прогонам
 * на стенде, а не по тому, что происходит на самом деле.
 *
 * Список слева, переписка справа — так проще листать подряд, а не возвращаться
 * назад после каждого диалога. Ничего не редактируется: это чужой разговор,
 * читаем и делаем выводы.
 */
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Loader2, MessageSquare, Phone } from 'lucide-react'

import { agentsApi } from '@/api/client'

const CHANNELS: { key: string; label: string }[] = [
  { key: '', label: 'Все' },
  { key: 'web', label: 'Сайт' },
  { key: 'telegram', label: 'Telegram' },
  { key: 'max', label: 'MAX' },
]

/** «13 авг, 12:05» — год не показываем: переписки смотрят свежие. */
function when(iso: string | null): string {
  if (!iso) return ''
  return new Date(iso).toLocaleString('ru-RU', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  })
}

/** Из `tenant:session` показываем только сессию — префикс одинаков у всех. */
function visitor(userId: string, name: string | null, username: string | null): string {
  if (name) return name
  if (username) return `@${username}`
  const tail = userId.includes(':') ? userId.slice(userId.indexOf(':') + 1) : userId
  return `Посетитель ${tail.slice(0, 12)}`
}

export function DialogsPanel({ slug }: { slug: string }) {
  const [channel, setChannel] = useState('')
  const [openId, setOpenId] = useState<string | null>(null)

  const list = useQuery({
    queryKey: ['agent-dialogs', slug, channel],
    queryFn: () => agentsApi.dialogs(slug, { channel, limit: 100 }),
  })

  const chat = useQuery({
    queryKey: ['agent-dialog', slug, openId],
    queryFn: () => agentsApi.dialogMessages(slug, openId as string),
    enabled: !!openId,
  })

  if (list.isLoading) {
    return (
      <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Загружаем переписки…
      </div>
    )
  }

  const items = list.data?.items ?? []

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex gap-1 rounded-md border p-0.5">
          {CHANNELS.map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={() => { setChannel(c.key); setOpenId(null) }}
              className={`px-3 py-1 text-xs rounded ${
                channel === c.key ? 'bg-accent font-medium' : 'text-muted-foreground'}`}
            >
              {c.label}
            </button>
          ))}
        </div>
        <span className="text-xs text-muted-foreground">
          всего переписок: {list.data?.total ?? 0}
        </span>
      </div>

      {items.length === 0 ? (
        <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
          Переписок пока нет. Здесь появится всё, что бот ответит живым людям —
          с сайта, из Telegram и MAX.
        </div>
      ) : (
        <div className="grid gap-3 lg:grid-cols-[minmax(0,380px)_minmax(0,1fr)]">
          {/* Список */}
          <div className="max-h-[560px] space-y-1 overflow-y-auto rounded-md border p-1">
            {items.map((d) => (
              <button
                key={d.id}
                type="button"
                onClick={() => setOpenId(d.id)}
                className={`w-full rounded px-3 py-2 text-left transition-colors ${
                  openId === d.id ? 'bg-accent' : 'hover:bg-muted'}`}
              >
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-medium">
                    {visitor(d.user_id, d.full_name, d.username)}
                  </span>
                  {d.handed_off && (
                    <Phone className="h-3 w-3 shrink-0 text-emerald-600" />
                  )}
                  <span className="ml-auto shrink-0 text-[11px] text-muted-foreground">
                    {when(d.updated_at || d.created_at)}
                  </span>
                </div>
                <div className="mt-0.5 line-clamp-2 text-[11px] leading-snug text-muted-foreground">
                  {d.last_role === 'assistant' ? 'Бот: ' : ''}{d.last_text}
                </div>
                <div className="mt-1 flex gap-2 text-[10px] text-muted-foreground">
                  <span>{d.channel === 'web' ? 'сайт' : d.channel}</span>
                  <span>{d.messages} сообщ.</span>
                  {d.qualification && <span>{d.qualification}</span>}
                </div>
              </button>
            ))}
          </div>

          {/* Переписка */}
          <div className="max-h-[560px] overflow-y-auto rounded-md border p-3">
            {!openId ? (
              <div className="flex h-full items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
                <MessageSquare className="h-4 w-4" /> Выберите переписку слева
              </div>
            ) : chat.isLoading ? (
              <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Открываем…
              </div>
            ) : (
              <div className="space-y-2">
                {(chat.data?.messages ?? []).map((m, i) => (
                  <div
                    key={i}
                    className={`max-w-[85%] whitespace-pre-wrap rounded-lg px-3 py-2 text-sm ${
                      m.role === 'assistant'
                        ? 'bg-muted'
                        : 'ml-auto bg-slate-900 text-white'}`}
                  >
                    {m.content}
                    <div className={`mt-1 text-[10px] ${
                      m.role === 'assistant' ? 'text-muted-foreground' : 'text-white/60'}`}>
                      {when(m.created_at)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
